import AddIcon from "@mui/icons-material/Add";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { Box, Button, Tab, Tabs, useTheme } from "@mui/material";
import { useState } from "react";

import { useActorVoicesQuery } from "Actors/Api/Queries/useActorVoicesQuery";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type BlDetailSectionItem,
  DetailSection,
} from "Shared/Sections/DetailSection";
import { ActorTransactionsTab } from "./ActorTransactionsTab";
import { ActorVoiceListItem } from "./ActorVoiceListItem";
import { CreateActorVoiceModal } from "./CreateActorVoiceModal";
import { EditActorVoiceModal } from "./EditActorVoiceModal";

interface ActorVoicesSectionProps {
  actorID: string;
}

export const ActorVoicesSection = ({
  actorID,
}: ActorVoicesSectionProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingVoiceID, setEditingVoiceID] = useState<string | null>(null);

  const { data: voices, isLoading: isLoadingVoices } =
    useActorVoicesQuery(actorID);

  const handleVoiceCreated = (actorVoiceID: string) => {
    setIsCreateModalOpen(false);
    setEditingVoiceID(actorVoiceID);
  };

  const languageTranslationMap: Record<string, string> = {
    Czech: t(Resources.Common.Languages.Czech),
    English: t(Resources.Common.Languages.English),
    Deutsch: t(Resources.Common.Languages.Deutsch),
  };

  const voiceItems: BlDetailSectionItem[] = (voices ?? []).map((voice) => {
    const languageName = voice.language
      ? (languageTranslationMap[voice.language] ?? voice.language)
      : "";

    return {
      id: voice.actorVoiceID,
      startAdornment: (
        <DetailSection.IconAdornment>
          <RecordVoiceOverIcon />
        </DetailSection.IconAdornment>
      ),
      label: {
        text: voice.name,
        secondaryText: `(${t(Resources.Actors.ActorDetail.Trained)}: ${languageName})`,
      },
      endAdornment: (
        <ActorVoiceListItem
          voice={voice}
          actorID={actorID}
          onEdit={() => setEditingVoiceID(voice.actorVoiceID)}
        />
      ),
    };
  });

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
        sx={{
          mb: Spacing.M,
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.text.secondary,
          },
          "& .Mui-selected": {
            color: `${theme.palette.text.primary} !important`,
          },
        }}
      >
        <Tab label={t(Resources.Actors.ActorDetail.Voices)} />
        <Tab label={t(Resources.Actors.ActorDetail.Videos)} disabled />
        <Tab label={t(Resources.Actors.ActorDetail.Transactions)} />
      </Tabs>

      {activeTab === 0 && (
        <DetailSection
          title={t(Resources.Actors.ActorDetail.TrainedVoices)}
          action={
            <Button
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t(Resources.Actors.ActorDetail.NewVoice)}
            </Button>
          }
          items={voiceItems}
          isLoading={isLoadingVoices}
          emptyMessage={t(Resources.Actors.ActorDetail.NoTrainedVoices)}
        />
      )}

      {activeTab === 2 && <ActorTransactionsTab actorID={actorID} />}

      <CreateActorVoiceModal
        isOpen={isCreateModalOpen}
        actorID={actorID}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={handleVoiceCreated}
      />
      {editingVoiceID && (
        <EditActorVoiceModal
          isOpen
          actorID={actorID}
          actorVoiceID={editingVoiceID}
          onClose={() => setEditingVoiceID(null)}
        />
      )}
    </Box>
  );
};
